import React, { useEffect, useRef, useState } from "react";
import Svg from "../../../../common/Svg";

function getRovingTabIndex(selectedIndex, index) {
  if (selectedIndex === index) {
    return 0;
  }
  if (selectedIndex === null && index === 0) {
    return 0;
  }
  return -1;
}

function handleRadioKeyDown(
  event,
  { index, count, onSelect, onFocusIndex, onExitDown, onExitUp }
) {
  switch (event.key) {
    case "Enter":
    case " ":
      event.preventDefault();
      onSelect(index);
      return;
    case "ArrowDown":
    case "ArrowRight":
      event.preventDefault();
      if (onExitDown) {
        onExitDown();
        return;
      }
      onFocusIndex(Math.min(index + 1, count - 1));
      return;
    case "ArrowUp":
    case "ArrowLeft":
      event.preventDefault();
      if (onExitUp) {
        onExitUp();
        return;
      }
      onFocusIndex(Math.max(index - 1, 0));
      return;
    default:
      break;
  }
}

/**
 * Parent cards use hidden radios + CSS selection. Local state gives instant
 * feedback; getLiveParentIndex reads module cache when Puck re-mounts after renderChanges.
 */
export function NestedRadioGroupField({
  label,
  showLabel,
  required,
  errorMessage,
  items,
  ID,
  hide,
  hint,
  hintStyle,
  getLiveParentIndex,
  getLiveChildIndex,
  onParentSelect,
  onChildSelect,
}) {
  const [localParentIndex, setLocalParentIndex] = useState(null);
  const [localChildIndex, setLocalChildIndex] = useState(undefined);
  const parentRefs = useRef([]);
  const childRefs = useRef([]);
  const pendingChildFocusRef = useRef(false);

  const liveParentIndex = getLiveParentIndex();
  const resolvedParentIndex =
    localParentIndex !== null ? localParentIndex : liveParentIndex;

  const selectedParent =
    resolvedParentIndex !== null && resolvedParentIndex >= 0
      ? items[resolvedParentIndex]
      : null;
  const nestedChildren = Array.isArray(selectedParent?.children)
    ? selectedParent.children
    : [];

  const liveChildIndex = getLiveChildIndex(nestedChildren);
  const resolvedChildIndex =
    localChildIndex !== undefined
      ? localChildIndex >= 0
        ? localChildIndex
        : null
      : liveChildIndex;

  useEffect(() => {
    if (!pendingChildFocusRef.current || nestedChildren.length === 0) {
      return;
    }
    pendingChildFocusRef.current = false;
    childRefs.current[0]?.focus();
  }, [resolvedParentIndex, nestedChildren.length]);

  if (hide) {
    return null;
  }

  const inputOpacity = hide ? 0.5 : 1;

  const getItemValue = (item, index) =>
    item?.value ?? item?.RadioOption ?? String(index);

  const focusParent = (index) => {
    parentRefs.current[index]?.focus();
  };

  const focusChild = (index) => {
    childRefs.current[index]?.focus();
  };

  const handleParentSelect = (
    parentValue,
    index,
    hasChildren,
    focusFirstChild = false
  ) => {
    setLocalParentIndex(index);
    setLocalChildIndex(-1);
    onParentSelect(parentValue, index, hasChildren);
    if (hasChildren && focusFirstChild) {
      pendingChildFocusRef.current = true;
    }
  };

  const handleChildSelect = (childValue, index) => {
    setLocalChildIndex(index);
    onChildSelect(childValue, index);
  };

  const parentGroupLabel =
    typeof label === "string" && label.trim().length > 0 ? label : undefined;

  return (
    <div
      className="roi-form-group position-relative"
      style={{ opacity: inputOpacity }}
    >
      {showLabel !== false && label && (
        <label className="field-label">
          {label} {required && <span className="required">*</span>}{" "}
          {hint && (
            <div className={`hint ${hintStyle}`}>
              <button type="button" className="info">
                <Svg name="hint-button" />
              </button>
              <div
                className="popover"
                dangerouslySetInnerHTML={{ __html: hint }}
              ></div>
            </div>
          )}{" "}
        </label>
      )}
      {showLabel === false && hint && (
        <div className={`hint ${hintStyle}`}>
          <button type="button" className="info">
            <Svg name="hint-button" />
          </button>
          <div
            className="popover"
            dangerouslySetInnerHTML={{ __html: hint }}
          ></div>
        </div>
      )}

      <div
        className="roi-nested-radio-group"
        role="radiogroup"
        aria-label={parentGroupLabel}
        aria-required={required || undefined}
      >
        {items.map((item, i) => {
          const isParentSelected = resolvedParentIndex === i;
          const parentChildren = Array.isArray(item.children) ? item.children : [];
          const hasChildren = parentChildren.length > 0;
          const parentValue = getItemValue(item, i);
          const parentId = `${ID}-parent-${i}`;

          return (
            <React.Fragment key={i}>
              <div
                ref={(element) => {
                  parentRefs.current[i] = element;
                }}
                id={parentId}
                className={[
                  "roi-nested-radio-parent",
                  isParentSelected && "is-selected",
                ]
                  .filter(Boolean)
                  .join(" ")}
                role="radio"
                aria-checked={isParentSelected}
                tabIndex={getRovingTabIndex(resolvedParentIndex, i)}
                onClick={() => handleParentSelect(parentValue, i, hasChildren)}
                onKeyDown={(event) =>
                  handleRadioKeyDown(event, {
                    index: i,
                    count: items.length,
                    onSelect: (nextIndex) => {
                      const nextItem = items[nextIndex];
                      const nextHasChildren = Array.isArray(nextItem?.children)
                        ? nextItem.children.length > 0
                        : false;
                      const nextValue = getItemValue(nextItem, nextIndex);
                      handleParentSelect(
                        nextValue,
                        nextIndex,
                        nextHasChildren,
                        event.key === "Enter" || event.key === " "
                      );
                    },
                    onFocusIndex: (nextIndex) => {
                      const nextItem = items[nextIndex];
                      const nextHasChildren = Array.isArray(nextItem?.children)
                        ? nextItem.children.length > 0
                        : false;
                      handleParentSelect(
                        getItemValue(nextItem, nextIndex),
                        nextIndex,
                        nextHasChildren
                      );
                      focusParent(nextIndex);
                    },
                    onExitDown:
                      isParentSelected && hasChildren
                        ? () => focusChild(0)
                        : undefined,
                  })
                }
              >
                <input
                  className="roi-nested-radio-parent__input"
                  type="radio"
                  name={ID}
                  value={parentValue}
                  checked={isParentSelected}
                  readOnly
                  tabIndex={-1}
                  aria-hidden="true"
                />
                <span className="roi-nested-radio-parent__title">
                  {item.RadioOption}
                </span>
              </div>

              {isParentSelected && hasChildren && (
                <div
                  className="roi-nested-radio-children"
                  role="radiogroup"
                  aria-label={`${item.RadioOption} options`}
                >
                  {parentChildren.map((child, j) => {
                    const childValueResolved = getItemValue(child, j);
                    const isChildSelected = resolvedChildIndex === j;
                    const childId = `${ID}-child-${i}-${j}`;

                    return (
                      <div
                        key={j}
                        ref={(element) => {
                          childRefs.current[j] = element;
                        }}
                        id={childId}
                        className={[
                          "roi-nested-radio-child",
                          isChildSelected && "is-selected",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        role="radio"
                        aria-checked={isChildSelected}
                        tabIndex={getRovingTabIndex(resolvedChildIndex, j)}
                        onClick={() =>
                          handleChildSelect(childValueResolved, j)
                        }
                        onKeyDown={(event) =>
                          handleRadioKeyDown(event, {
                            index: j,
                            count: parentChildren.length,
                            onSelect: (nextIndex) => {
                              handleChildSelect(
                                getItemValue(parentChildren[nextIndex], nextIndex),
                                nextIndex
                              );
                            },
                            onFocusIndex: (nextIndex) => {
                              handleChildSelect(
                                getItemValue(parentChildren[nextIndex], nextIndex),
                                nextIndex
                              );
                              focusChild(nextIndex);
                            },
                            onExitUp: j === 0 ? () => focusParent(i) : undefined,
                          })
                        }
                      >
                        <span className="roi-nested-radio-child__label">
                          {child.RadioOption}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
      {errorMessage && <div className="input-error">{errorMessage}</div>}
    </div>
  );
}
